// src/pages/qc/PageQCRmSpecs.jsx
import { useState, useEffect, useMemo } from "react";
import * as api from "../../apiService";
import { RmSpecParamModal } from "./RmSpecParamModal";
import { useToast } from "../../components/Toast";

export function PageQCRmSpecs({ rms }) {
  const showToast = useToast();
  const [rmSpecs,    setRmSpecs]    = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState(null);
  const [search,     setSearch]     = useState("");
  const [editRmId,   setEditRmId]   = useState(null);
  const [paramModal, setParamModal] = useState(null);

  useEffect(() => {
    api.fetchRmSpecs()
      .then(groups => groups.flatMap(g => g.params))
      .then(setRmSpecs)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  const grouped = useMemo(() => {
    const m = {};
    rmSpecs.forEach(s => { if (!m[s.rmId]) m[s.rmId] = []; m[s.rmId].push(s); });
    return m;
  }, [rmSpecs]);

  const specRmIds = useMemo(() => Object.keys(grouped).sort((a,b) => a.localeCompare(b)), [grouped]);

  const rmsWithoutSpec = useMemo(() => {
    const withSpec = new Set(specRmIds);
    return rms.filter(r => !withSpec.has(r.id));
  }, [rms, specRmIds]);

  const filteredIds = useMemo(() =>
    specRmIds.filter(id => {
      const q  = search.toLowerCase();
      const rm = rms.find(r => r.id === id);
      return id.toLowerCase().includes(q) || (rm?.name ?? "").toLowerCase().includes(q);
    }),
    [specRmIds, search, rms]);

  const getParam  = (params, name) => params.find(p => p.parameter === name);
  const qualVal   = (params, name) => getParam(params, name)?.expectedValue ?? "—";
  const numRange  = (params, name) => {
    const p = getParam(params, name);
    if (!p) return "—";
    if (p.minValue != null && p.maxValue != null) return `${p.minValue} – ${p.maxValue}`;
    return p.expectedValue ?? "—";
  };
  const viscRange = (params) => {
    const p = getParam(params, "Viscosity");
    if (!p) return "—";
    if (p.minValue != null && p.maxValue != null)
      return `${p.minValue} – ${p.maxValue}${p.uom ? " " + p.uom : ""}`;
    return p.expectedValue ?? "—";
  };

  async function handleAddParam(rmId, form) {
    const saved = await api.addRmSpecParam(rmId, form);
    setRmSpecs(old => [...old, saved]);
    showToast("Parameter added");
    setParamModal(null);
  }
  async function handleEditParam(specId, form) {
    const saved = await api.updateRmSpecParam(specId, form);
    setRmSpecs(old => old.map(s => s.id === specId ? saved : s));
    showToast("Parameter updated");
    setParamModal(null);
  }
  async function handleDeleteParam(specId) {
    await api.deleteRmSpecParam(specId);
    setRmSpecs(old => old.filter(s => s.id !== specId));
    showToast("Parameter deleted", "info");
  }

  const editRmParams = editRmId ? (grouped[editRmId] || []) : [];
  const editRm       = editRmId ? rms.find(r => r.id === editRmId) : null;

  return (
    <>
      <div className="page-header">
        <div className="page-header-left">
          <div className="page-title">Raw Material Specifications</div>
          <div className="page-subtitle">Raw material acceptance parameters</div>
        </div>
        <div className="page-header-right">
          <button className="btn btn-primary" onClick={() => setParamModal({ type:"addRm" })}>+ Add RM Spec</button>
        </div>
      </div>
      <div className="action-bar">
        <input className="form-input" placeholder="Search RM…" value={search}
          onChange={e => setSearch(e.target.value)} style={{ width:220 }} />
      </div>

      {loading && <div style={{ color:"#aaa", fontSize:12 }}>Loading…</div>}
      {error   && <div className="alert-warn">Error: {error}</div>}

      {!loading && !error && (
        <div className="table-wrap">
          <table className="data-table">
            <thead><tr>
              {["Grade","Appearance","Color","Odor","pH","Density (g/mL)","Solid Content (%)","Viscosity",""].map((h,i) => (
                <th key={i}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {filteredIds.map(rmId => {
                const rm     = rms.find(r => r.id === rmId);
                const params = grouped[rmId] || [];
                return (
                  <tr key={rmId}>
                    <td className="fw-600">
                      {rmId}
                      {rm && <div className="text-muted" style={{ fontSize:11, fontWeight:400 }}>{rm.name}</div>}
                    </td>
                    <td>{qualVal(params, "Appearance")}</td>
                    <td>{qualVal(params, "Color")}</td>
                    <td>{qualVal(params, "Odor")}</td>
                    <td>{numRange(params, "pH")}</td>
                    <td>{numRange(params, "Density")}</td>
                    <td>{numRange(params, "Solid Content")}</td>
                    <td>{viscRange(params)}</td>
                    <td>
                      <button className="btn-small" onClick={() => setEditRmId(rmId)}>Edit</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Edit RM params panel ── */}
      {editRmId && (
        <div className="overlay" onClick={e => { if (e.target===e.currentTarget) setEditRmId(null); }}>
          <div className="modal" style={{ width:680, maxWidth:"95vw" }}>
            <div className="modal-header">
              <h3 className="modal-title">
                {editRmId}{editRm ? ` — ${editRm.name}` : ""}
              </h3>
              <button className="btn" onClick={() => setEditRmId(null)}>✕</button>
            </div>
            <div className="table-wrap" style={{ maxHeight:300, overflowY:"auto" }}>
              <table className="data-table" style={{ marginBottom:10 }}>
                <thead><tr>
                  <th>Parameter</th><th>UOM</th><th>Min</th><th>Max</th><th>Expected Value</th><th>Test Method</th><th></th>
                </tr></thead>
                <tbody>
                  {editRmParams.map(p => (
                    <tr key={p.id}>
                      <td className="fw-600">{p.parameter}</td>
                      <td>{p.uom || "—"}</td>
                      <td>{p.minValue ?? "—"}</td>
                      <td>{p.maxValue ?? "—"}</td>
                      <td>{p.expectedValue ?? "—"}</td>
                      <td>{p.testMethod ?? "—"}</td>
                      <td style={{ display:"flex", gap:6 }}>
                        <button className="btn-small" onClick={() => setParamModal({ type:"editParam", data:p })}>Edit</button>
                        <button className="btn-small" style={{ color:"#dc2626" }}
                          onClick={() => { if (window.confirm(`Delete "${p.parameter}"?`)) handleDeleteParam(p.id); }}>✕</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="form-footer" style={{ marginTop:8 }}>
              <button className="btn btn-primary" style={{ fontSize:11, padding:"4px 12px" }}
                onClick={() => setParamModal({ type:"addParam", data:{ rmId: editRmId } })}>
                + Add Parameter
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Select RM to add spec ── */}
      {paramModal?.type === "addRm" && (
        <div className="overlay" onClick={e => { if (e.target===e.currentTarget) setParamModal(null); }}>
          <div className="modal">
            <div className="modal-header">
              <h3 className="modal-title">Add RM Specification</h3>
              <button className="btn" onClick={() => setParamModal(null)}>✕</button>
            </div>
            <div className="form-row">
              <span className="form-label">Raw Material</span>
              <select className="form-select" style={{ flex:1 }}
                onChange={e => { if (e.target.value) setParamModal({ type:"addParam", data:{ rmId:e.target.value } }); }}>
                <option value="">Select RM…</option>
                {rmsWithoutSpec.map(r => <option key={r.id} value={r.id}>{r.id} — {r.name}</option>)}
              </select>
            </div>
            <div className="form-footer" style={{ marginTop:12 }}>
              <button className="btn" onClick={() => setParamModal(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Add / edit individual param ── */}
      {(paramModal?.type === "addParam" || paramModal?.type === "editParam") && (
        <div className="overlay" onClick={e => { if (e.target===e.currentTarget) setParamModal(null); }}>
          <RmSpecParamModal
            rmId={paramModal.data?.rmId ?? editRmId}
            param={paramModal.type === "editParam" ? paramModal.data : null}
            onSave={form => paramModal.type === "editParam"
              ? handleEditParam(paramModal.data.id, form)
              : handleAddParam(paramModal.data?.rmId ?? editRmId, form)}
            onCancel={() => setParamModal(null)}
          />
        </div>
      )}
    </>
  );
}
